import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";

import { toast } from "sonner";
import z from "zod";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  adminCompleteReview,
  adminRejectReview,
  adminStartReview,
  invalidateReviewRequestQueries,
} from "@/lib/client/review";

export const AdminActions = {
  ReviewCompletionButton({ id }: { id: string }) {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);

    const { mutate, isPending } = useMutation({
      mutationFn: adminCompleteReview,
      onSuccess() {
        toast.success("已发送复盘反馈～");
        localStorage.removeItem(`review-feedback-${id}`);
        // 草稿已清除，同步清空表单，避免再次打开时显示旧反馈
        form.reset({ feedback: "" });
        invalidateReviewRequestQueries(queryClient, "admin");
        setOpen(false);
      },
      onError(error) {
        toast.error(error.message);
      },
    });

    const form = useForm({
      defaultValues: {
        feedback: localStorage.getItem(`review-feedback-${id}`) || "",
      },
      validators: {
        onChange: z.object({
          feedback: z.string(),
        }),
      },
      listeners: {
        onChange(props) {
          try {
            const value = props.formApi.getFieldValue("feedback");
            localStorage.setItem(`review-feedback-${id}`, value);
          } catch {}
        },
      },
      onSubmit({ value }) {
        mutate({ data: { id, ...value } });
      },
    });

    return (
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger nativeButton={true} render={<Button className="">完成复盘</Button>} />
        <AlertDialogContent className="data-[size=default]:sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>完成复盘</AlertDialogTitle>
            <AlertDialogDescription>确定后转为已复盘状态。</AlertDialogDescription>
            <form
              className="w-full justify-self-stretch"
              id={`review-completion-form-${id}`}
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
              }}
            >
              <form.Field name="feedback">
                {({ state, handleChange, handleBlur }) => (
                  <Textarea
                    value={state.value}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    placeholder="填写复盘反馈..."
                    className="w-full max-w-full field-sizing-fixed"
                  />
                )}
              </form.Field>
            </form>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>手滑了</AlertDialogCancel>
            <Button form={`review-completion-form-${id}`} type="submit" disabled={isPending}>
              {isPending && <Loader2 className="animate-spin" />}
              确定
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  },

  ReviewConfirmationButton({ id }: { id: string }) {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const { mutate, isPending } = useMutation({
      mutationFn: adminStartReview,
      onSuccess() {
        toast.success("开始复盘");
        invalidateReviewRequestQueries(queryClient, "admin");
        setOpen(false);
      },
      onError(error) {
        toast.error(error.message);
      },
    });
    return (
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger nativeButton={true} render={<Button className="">开始复盘</Button>} />
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确定要开始复盘吗？</AlertDialogTitle>
            <AlertDialogDescription>确定后转为复盘中状态。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>手滑了</AlertDialogCancel>
            <Button disabled={isPending} onClick={() => mutate({ data: { id } })}>
              {isPending && <Loader2 className="animate-spin" />}
              确定
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  },

  ReviewRejectionButton({ id }: { id: string }) {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const { mutate, isPending } = useMutation({
      mutationFn: adminRejectReview,
      onSuccess() {
        toast.success("拒绝成功");
        localStorage.removeItem(`review-reject-reason-${id}`);
        // 草稿已清除，同步清空表单，避免再次打开时显示旧理由
        form.reset({ rejectReason: "" });
        invalidateReviewRequestQueries(queryClient, "admin");
        setOpen(false);
      },
      onError(error) {
        toast.error(error.message);
      },
    });
    const form = useForm({
      defaultValues: {
        rejectReason: localStorage.getItem(`review-reject-reason-${id}`) || "",
      },
      validators: {
        onChange: z.object({
          rejectReason: z.string(),
        }),
      },
      listeners: {
        onChange(props) {
          try {
            const value = props.formApi.getFieldValue("rejectReason");
            localStorage.setItem(`review-reject-reason-${id}`, value);
          } catch {}
        },
      },
      onSubmit({ value }) {
        mutate({ data: { id, ...value } });
      },
    });
    return (
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger
          nativeButton={true}
          render={
            <Button className="" variant="destructive">
              拒绝复盘
            </Button>
          }
        />
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>拒绝复盘</AlertDialogTitle>
            <AlertDialogDescription>确定后转为已拒绝状态。</AlertDialogDescription>
            <form
              className="w-full justify-self-stretch"
              id={`review-rejection-form-${id}`}
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
              }}
            >
              <form.Field name="rejectReason">
                {({ state, handleChange, handleBlur }) => (
                  <Input
                    value={state.value}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    id="rejectReason"
                    placeholder="拒绝理由..."
                  />
                )}
              </form.Field>
            </form>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>手滑了</AlertDialogCancel>
            <Button form={`review-rejection-form-${id}`} type="submit" disabled={isPending}>
              {isPending && <Loader2 className="animate-spin" />}
              确定
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  },
};
